<template>
  <div v-if="open" class="v2-modal" role="dialog" aria-modal="true" :aria-labelledby="titleId">
    <div class="v2-modal__backdrop" @click="onBackdrop" />
    <div class="v2-modal__panel" :class="sizeClass" ref="panel">
      <header v-if="title || $slots.header" class="v2-modal__header">
        <slot name="header">
          <h2 :id="titleId" class="v2-modal__title">{{ title }}</h2>
        </slot>
        <button
          type="button"
          class="v2-modal__close"
          aria-label="Cerrar"
          @click="close"
        >
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </header>
      <div class="v2-modal__body">
        <slot />
      </div>
      <footer v-if="$slots.footer" class="v2-modal__footer">
        <slot name="footer" />
      </footer>
    </div>
  </div>
</template>

<script>
let modalCounter = 0;
export default {
  name: "BaseModal",
  props: {
    open: { type: Boolean, default: false },
    title: { type: String, default: "" },
    size: {
      type: String,
      default: "md",
      validator: (v) => ["sm", "md", "lg", "xl"].includes(v),
    },
    closeOnBackdrop: { type: Boolean, default: true },
  },
  data() {
    modalCounter += 1;
    return { titleId: `v2-modal-title-${modalCounter}` };
  },
  computed: {
    sizeClass() {
      return `v2-modal__panel--${this.size}`;
    },
  },
  watch: {
    open(value) {
      this.toggleBodyScroll(value);
    },
  },
  mounted() {
    this.toggleBodyScroll(this.open);
  },
  beforeDestroy() {
    this.toggleBodyScroll(false);
  },
  methods: {
    onBackdrop() {
      if (this.closeOnBackdrop) this.close();
    },
    close() {
      this.$emit("close");
    },
    toggleBodyScroll(active) {
      if (typeof document === "undefined") return;
      document.body.style.overflow = active ? "hidden" : "";
    },
  },
};
</script>

<style scoped>
.v2-modal {
  position: fixed;
  inset: 0;
  z-index: var(--z-modal);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-4);
}

.v2-modal__backdrop {
  position: absolute;
  inset: 0;
  background-color: rgba(15, 27, 50, 0.40);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.v2-modal__panel {
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - var(--space-8));
  background-color: var(--color-surface);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-xl);
  width: 100%;
  overflow: hidden;
}

.v2-modal__panel--sm { max-width: 420px; }
.v2-modal__panel--md { max-width: 560px; }
.v2-modal__panel--lg { max-width: 720px; }
.v2-modal__panel--xl { max-width: 920px; }

.v2-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-5);
  border-bottom: 1px solid var(--color-border);
}

.v2-modal__title {
  font-family: var(--font-headline);
  font-size: var(--fs-display-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text);
  margin: 0;
  line-height: var(--lh-snug);
}

.v2-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  background: transparent;
  color: var(--color-text-muted);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: background-color var(--transition-fast), color var(--transition-fast);
}

.v2-modal__close:hover {
  background-color: var(--color-surface-low);
  color: var(--color-text);
}

.v2-modal__close:focus-visible {
  outline: 2px solid var(--color-amber);
  outline-offset: 2px;
}

.v2-modal__body {
  padding: var(--space-5);
  overflow-y: auto;
  flex: 1 1 auto;
  color: var(--color-text);
}

.v2-modal__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-2);
  padding: var(--space-4) var(--space-5);
  border-top: 1px solid var(--color-border);
  background-color: var(--color-neutral-50);
  flex-wrap: wrap;
}
</style>
